import React from 'react';
import { View, Text, StyleSheet, TextStyle, ViewStyle } from 'react-native';
import { typography, spacing } from '../../tokens';
import { Colors } from '../../../../themes/colors';

export type StatusTagVariant = 'success' | 'neutral' | 'warning' | 'error' | 'info';

export interface StatusTagProps {
  /**
   * The status variant that determines the color scheme
   */
  variant: StatusTagVariant;

  /**
   * The text content to display
   */
  text: string;

  /**
   * Optional style overrides for the container
   */
  style?: ViewStyle;

  /**
   * Optional style overrides for the text
   */
  textStyle?: TextStyle;

  /**
   * Test ID for testing
   */
  testID?: string;
}

const variantStyles: Record<StatusTagVariant, { backgroundColor: string; textColor: string }> = {
  success: {
    backgroundColor: '#D3F4E7',
    textColor: '#24835D',
  },
  neutral: {
    backgroundColor: Colors.role.surfaceRaised,
    textColor: Colors.role.textSoft,
  },
  warning: {
    backgroundColor: '#FFF4C1',
    textColor: '#D7A321',
  },
  error: {
    backgroundColor: '#FFD9D2',
    textColor: '#E24D2A',
  },
  info: {
    backgroundColor: '#D0E5F9',
    textColor: '#4A90E2',
  },
};

export const StatusTag: React.FC<StatusTagProps> = ({
  variant,
  text,
  style,
  textStyle,
  testID,
}) => {
  const { backgroundColor, textColor } = variantStyles[variant];

  return (
    <View
      style={[styles.container, { backgroundColor }, style]}
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={`${variant} status: ${text}`}
    >
      <Text style={[styles.text, { color: textColor }, textStyle]}>{text}</Text>
    </View>
  );
};

StatusTag.displayName = 'StatusTag';

const styles = StyleSheet.create({
  container: {
    height: 24,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 99, // Full pill shape
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  text: {
    ...typography.label.medium,
  },
});
